import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { WizardState } from '../api';
import { SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import { Button, Pill, Spinner } from '../ui/primitives';

export function StepDone({ wizard }: { wizard: WizardState }) {
  const qc = useQueryClient();
  const finish = useMutation({
    mutationFn: () => api.post<WizardState>('/api/wizard/complete'),
    onSuccess: () => void qc.invalidateQueries(),
  });

  return (
    <div className="space-y-4">
      <MetricStrip
        section="accounts"
        items={[
          { label: 'Uplines', value: String(wizard.counts.uplineParties) },
          { label: 'Clients', value: String(wizard.counts.clientParties) },
          { label: 'Accounts', value: String(wizard.counts.accounts) },
        ]}
      />

      <ActionQueueCard
        section="accounts"
        eyebrow="Ready"
        title="Setup is ready for daily work"
        description="You can keep editing parties, accounts, aliases, and balances later. The wizard is only the first guided pass."
        meta={
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span>Aliases</span>
              <span className="font-medium text-ink">{wizard.counts.aliases}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Assignments</span>
              <span className="font-medium text-ink">{wizard.counts.assignments}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Opening balances</span>
              <span className="font-medium text-ink">{wizard.counts.openingBalances}</span>
            </div>
          </div>
        }
        actions={
          !wizard.completed ? (
            <Button onClick={() => finish.mutate()} disabled={finish.isPending}>
              {finish.isPending ? <Spinner /> : 'Finish setup'}
            </Button>
          ) : undefined
        }
      />

      <SectionCard section="accounts" title="Final check">
        <div className="space-y-3 text-[13px] leading-5 text-ink-muted">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={wizard.completed ? 'green' : 'amber'}>
              {wizard.completed ? 'Setup complete' : 'One last confirmation needed'}
            </Pill>
          </div>
          <div>
            Once you finish, the app will route you into the normal operating flow. Batches handles weekly import, Ledgers tracks balances, Cash records direct money movement, and Match handles split posting.
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
